import * as _ from 'lodash';
import * as utils from 'common/utils';
import { getAmplitudeTypeYAxisUnit } from 'common/utils';
import { transformSpectrumCoordinates } from 'common/utils/spectrumTransformations';
import {
  OVERLAY_TRACE_KEYS,
  getOverlayTraceKeysForChartType,
  isWaveformResponseError,
  isSpectrumResponseError,
  pointHasTraceData
} from 'utils/overlayTraceUtils';
import {
  buildOverlayFetchKey,
  createOverlayFetchCoordinator,
  runWithConcurrencyLimit
} from 'utils/overlayFetchCoordinator';
import { areTimestampsEqual } from './overlayPointSelection';

export function createOverlayTraceController({ getState, setState, getProps, isMounted, getShaftSpeed }) {
  const { beginOverlayFetch, isOverlayFetchCurrent, endOverlayFetch, clearAllOverlayFetches } =
    createOverlayFetchCoordinator();

  let overlayEpoch = 0;

  const bumpOverlayEpoch = () => {
    overlayEpoch += 1;
    clearAllOverlayFetches();
    return overlayEpoch;
  };

  const isOverlayOperationCurrent = (tagId, epoch) =>
    isMounted() &&
    getState().tag &&
    getState().tag.id === tagId &&
    overlayEpoch === epoch;

  const logOverlayFetchError = (context, details) => {
    const tagId = getState().tag && getState().tag.id;
    const payload = {
      context,
      tagId,
      timestamp: details && details.timestamp,
      message: (details && (details.message || details.error)) || 'unknown'
    };
    console.warn('Overlay trace fetch failed:', payload);
  };

  const findPointIndexByTimestamp = (points, timestamp) => {
    if (!points || !timestamp) return -1;
    return points.findIndex((point) => {
      if (!point.x) return false;
      if (point.x.i) {
        return areTimestampsEqual(point.x.i, timestamp);
      }
      return areTimestampsEqual(point.x, timestamp);
    });
  };

  const findPointIndexByMeasurementId = (points, measurementId) => {
    if (!points || measurementId == null) return -1;
    return points.findIndex(point => point.measurement_id === measurementId);
  };

  // Resolve a fetch back to its originating point. measurement_id is a stable
  // per-point identity, so prefer it: if the point was deselected mid-flight,
  // this returns -1 and the stale response is discarded instead of merging into
  // a different point that happens to share the same timestamp (the C3 race).
  // Falls back to timestamp matching only when no measurement_id was captured.
  const findResolvePointIndex = (points, measurementId, timestamp) => {
    if (measurementId != null) {
      return findPointIndexByMeasurementId(points, measurementId);
    }
    return findPointIndexByTimestamp(points, timestamp);
  };

  const getPointTraceStatus = (point, traceType) => {
    if (!point || !point.traceStatus) return 'idle';
    return point.traceStatus[traceType] || 'idle';
  };

  const updatePointTraceStatus = (timestamp, traceType, status) => {
    setState((prevState) => {
      const pointIndex = findPointIndexByTimestamp(prevState.selectedPoints, timestamp);
      if (pointIndex === -1) return null;
      const updatedPoints = [...prevState.selectedPoints];
      updatedPoints[pointIndex] = {
        ...updatedPoints[pointIndex],
        traceStatus: {
          ...(updatedPoints[pointIndex].traceStatus || {}),
          [traceType]: status
        }
      };
      return { selectedPoints: updatedPoints };
    });
  };

  const buildOverlayFetchParams = (point, overrides = {}) => {
    const { amp_type, feature, tag } = getState();
    let params = {
      feature,
      amp_type,
      timestamp: point && point.x ? (point.x.i || point.x) : undefined,
      ...overrides
    };
    params = utils.sanitizeAmpTypeParam(params, tag);
    if (params.amp_type === 'demod') {
      params.amp_type = 'acceleration';
      params.type = 'demod';
    }
    if (params.amp_type === 'normal') params.amp_type = 'velocity';
    return params;
  };

  const mergeSpectrumOverlayIntoPoint = (prevState, pointIndex, response, forcingFrequencies, params, config) => {
    if (pointIndex === -1) {
      return null;
    }

    const originalResponse = _.cloneDeep(response);
    let currentYUnit = prevState.spectrumYUnit;
    if (!currentYUnit && prevState.selectedPoints && prevState.selectedPoints.length > 0) {
      const pointWithYUnit = prevState.selectedPoints.find(p => p.yUnit);
      if (pointWithYUnit && pointWithYUnit.yUnit) {
        currentYUnit = pointWithYUnit.yUnit;
      }
    }
    if (!currentYUnit) {
      currentYUnit = response.amp_units || '';
    }

    const preferredXUnit = params.currentXUnit || params.preserveXUnit ||
      prevState.spectrumXUnit ||
      (config.spectrum && config.spectrum.freq_units) || 'Hz';

    const originalSpectrumFeatures = { items: [] };
    if (config.originalSpectrumFeatures && config.originalSpectrumFeatures.items) {
      originalSpectrumFeatures.items = [...config.originalSpectrumFeatures.items];
    } else if (config.spectrumFeatures && config.spectrumFeatures.items) {
      originalSpectrumFeatures.items = [...config.spectrumFeatures.items];
    }
    if (forcingFrequencies && Array.isArray(forcingFrequencies)) {
      originalSpectrumFeatures.items = [...originalSpectrumFeatures.items, ...forcingFrequencies];
    }

    const originalSpectrumData = _.cloneDeep(response.spectrum_data);
    let pointSpectrumFeatures = _.cloneDeep(originalSpectrumFeatures);
    let processedSpectrumData = _.cloneDeep(originalSpectrumData);
    const targetPoint = prevState.selectedPoints[pointIndex];
    const shaftSpeed = response.shaft_speed ||
      (targetPoint && (targetPoint.shaft_speed || targetPoint.speed)) ||
      getShaftSpeed();

    if (currentYUnit && response.amp_units &&
      currentYUnit.toLowerCase() === 'db' && response.amp_units.toLowerCase() !== 'db') {
      const reference = utils.getAmplitudeConversionReference(response.amp_units, 'db');
      if (reference) {
        processedSpectrumData = processedSpectrumData.map(d => ({
          x: d.x,
          y: utils.convertToDbScale(d.y, reference)
        }));
      }
    } else if (currentYUnit && response.amp_units &&
      currentYUnit.toLowerCase() !== 'db' && response.amp_units.toLowerCase() === 'db') {
      const reference = utils.getAmplitudeConversionReference('db', currentYUnit);
      if (reference) {
        processedSpectrumData = processedSpectrumData.map(d => ({
          x: d.x,
          y: utils.convertFromDbScale(d.y, reference)
        }));
      }
    }

    if (preferredXUnit !== 'Hz' && preferredXUnit !== response.freq_units) {
      if (shaftSpeed > 0) {
        const transformationResult = transformSpectrumCoordinates({
          sourceUnit: response.freq_units,
          targetUnit: preferredXUnit,
          shaftSpeed,
          spectrumData: processedSpectrumData,
          originalData: null,
          features: pointSpectrumFeatures,
          originalFeatures: originalSpectrumFeatures
        });
        processedSpectrumData = transformationResult.spectrumData;
        pointSpectrumFeatures = transformationResult.features;
      }
    }

    const updatedPoints = [...prevState.selectedPoints];
    updatedPoints[pointIndex] = {
      ...updatedPoints[pointIndex],
      // spectrum_data also doubles as the per-point "trace fetched" signal (pointHasTraceData).
      spectrum_data: processedSpectrumData,
      yUnit: currentYUnit,
      xUnit: preferredXUnit,
      spectrumFeatures: pointSpectrumFeatures,
      shaft_speed: shaftSpeed,
      originalData: originalSpectrumData,
      originalFeatures: originalSpectrumFeatures,
      originalResponse,
      traceStatus: {
        ...(updatedPoints[pointIndex].traceStatus || {}),
        spectrum: 'ok'
      }
    };

    // Don't pin spectrumYUnit here — it caused stale 'db' to persist across a tag change
    // into a linear-backend context, mislabeling the data.
    return {
      selectedPoints: updatedPoints,
      config: {
        ...prevState.config,
        spectrum: {
          ...prevState.config.spectrum,
          spectrum_data: response.spectrum_data,
          freq_units: response.freq_units,
          amp_units: response.amp_units,
          fmax: response.fmax,
          lines_of_resolution: response.lines_of_resolution,
          shaft_speed: response.shaft_speed
        },
        originalSpectrumFeatures: prevState.config.originalSpectrumFeatures ||
          (config.spectrumFeatures ? _.cloneDeep(config.spectrumFeatures) : null)
      }
    };
  };

  const fetchOverlaySpectrumForPoint = (params) => {
    const { tag, config } = getState();
    const { getTagSpectrum } = getProps().assetDetailsActions;
    if (!tag || !params.timestamp) return Promise.resolve(null);

    const tagId = tag.id;
    const epoch = overlayEpoch;
    const fetchKey = buildOverlayFetchKey({
      tagId,
      configId: config.config_id,
      timestamp: params.timestamp,
      ampType: params.amp_type,
      traceKey: OVERLAY_TRACE_KEYS.SPECTRUM,
      type: params.type,
      feature: params.feature
    });
    const generation = beginOverlayFetch(fetchKey);
    const startIndex = findPointIndexByTimestamp(getState().selectedPoints, params.timestamp);
    const fetchMeasurementId = startIndex !== -1
      ? getState().selectedPoints[startIndex].measurement_id
      : undefined;
    updatePointTraceStatus(params.timestamp, 'spectrum', 'loading');

    return getTagSpectrum(tag.id, params, config.config_id).then((response) => {
      if (!isOverlayOperationCurrent(tagId, epoch) || !isOverlayFetchCurrent(fetchKey, generation)) {
        endOverlayFetch(fetchKey, generation);
        return null;
      }

      if (isSpectrumResponseError(response)) {
        endOverlayFetch(fetchKey, generation);
        updatePointTraceStatus(params.timestamp, 'spectrum', 'error');
        logOverlayFetchError('spectrum', { timestamp: params.timestamp, message: response && response.message });
        return null;
      }

      return getProps().spectrumFeatureActions.getForcingFrequencies(tag.id, params.timestamp)
        .then((forcingFrequencies) => {
          if (!isOverlayOperationCurrent(tagId, epoch) || !isOverlayFetchCurrent(fetchKey, generation)) {
            endOverlayFetch(fetchKey, generation);
            return null;
          }
          setState((prevState) => {
            const pointIndex = findResolvePointIndex(
              prevState.selectedPoints,
              fetchMeasurementId,
              params.timestamp
            );
            return mergeSpectrumOverlayIntoPoint(
              prevState,
              pointIndex,
              response,
              forcingFrequencies,
              params,
              prevState.config
            );
          });
          endOverlayFetch(fetchKey, generation);
          return response;
        });
    }).catch((error) => {
      endOverlayFetch(fetchKey, generation);
      if (!isOverlayOperationCurrent(tagId, epoch)) return null;
      updatePointTraceStatus(params.timestamp, 'spectrum', 'error');
      logOverlayFetchError('spectrum', { timestamp: params.timestamp, message: error && error.message });
      return null;
    });
  };

  const fetchOverlayWaveformForPoint = (params) => {
    const { tag, config } = getState();
    const { getTagWaveform } = getProps().assetDetailsActions;
    if (!tag || !params.timestamp) return Promise.resolve(null);

    const tagId = tag.id;
    const epoch = overlayEpoch;
    const fetchKey = buildOverlayFetchKey({
      tagId,
      configId: config.config_id,
      timestamp: params.timestamp,
      ampType: params.amp_type,
      traceKey: OVERLAY_TRACE_KEYS.WAVEFORM,
      type: params.type,
      feature: params.feature
    });
    const generation = beginOverlayFetch(fetchKey);
    const startIndex = findPointIndexByTimestamp(getState().selectedPoints, params.timestamp);
    const fetchMeasurementId = startIndex !== -1
      ? getState().selectedPoints[startIndex].measurement_id
      : undefined;
    updatePointTraceStatus(params.timestamp, 'waveform', 'loading');

    return getTagWaveform(tag.id, params, config.config_id).then((response) => {
      if (!isOverlayOperationCurrent(tagId, epoch) || !isOverlayFetchCurrent(fetchKey, generation)) {
        endOverlayFetch(fetchKey, generation);
        return null;
      }

      if (isWaveformResponseError(response)) {
        endOverlayFetch(fetchKey, generation);
        updatePointTraceStatus(params.timestamp, 'waveform', 'error');
        logOverlayFetchError('waveform', { timestamp: params.timestamp, message: response && response.message });
        return null;
      }

      setState((prevState) => {
        const pointIndex = findResolvePointIndex(
          prevState.selectedPoints,
          fetchMeasurementId,
          params.timestamp
        );
        if (pointIndex === -1) {
          return null;
        }

        const updatedPoints = [...prevState.selectedPoints];
        updatedPoints[pointIndex] = {
          ...updatedPoints[pointIndex],
          waveform_data: response.waveform_data,
          originalWaveformData: _.cloneDeep(response.waveform_data),
          waveform_amp_units: response.amp_units,
          amp_units: response.amp_units,
          sampling_rate: response.sampling_rate,
          fmax: response.fmax,
          lines_of_resolution: response.lines_of_resolution,
          traceStatus: {
            ...(updatedPoints[pointIndex].traceStatus || {}),
            waveform: 'ok'
          }
        };

        return {
          selectedPoints: updatedPoints,
          config: {
            ...prevState.config,
            waveform: {
              ...prevState.config.waveform,
              ...response
            }
          }
        };
      });
      endOverlayFetch(fetchKey, generation);
      return response;
    }).catch((error) => {
      endOverlayFetch(fetchKey, generation);
      if (!isOverlayOperationCurrent(tagId, epoch)) return null;
      updatePointTraceStatus(params.timestamp, 'waveform', 'error');
      logOverlayFetchError('waveform', { timestamp: params.timestamp, message: error && error.message });
      return null;
    });
  };

  const markOverlayTracesLoading = (points, traceType) => {
    if (!points || !points.length) return;
    const timestamps = new Set(
      points.map(p => (p.x && p.x.i) || p.x).filter(Boolean)
    );
    setState(prevState => ({
      selectedPoints: prevState.selectedPoints.map((point) => {
        const ts = (point.x && point.x.i) || point.x;
        if (!timestamps.has(ts)) return point;
        const cleared = traceType === 'waveform'
          ? { waveform_data: undefined }
          : { spectrum_data: undefined };
        return {
          ...point,
          ...cleared,
          traceStatus: {
            ...(point.traceStatus || {}),
            [traceType]: 'loading'
          }
        };
      })
    }));
  };

  const refreshOverlayPointsWithNewAmplitudeType = (newAmpType, previousAmpType, featureOverride) => {
    const { tag, config, selectedPoints } = getState();
    if (!tag || !selectedPoints.length) return;

    const unitSystem = (config && config.unit_system) || 1;
    const correctYUnit = getAmplitudeTypeYAxisUnit(newAmpType, unitSystem);
    if (correctYUnit) {
      setState({ spectrumYUnit: correctYUnit });
    }

    const pointsToRefresh = selectedPoints.filter(point => point.x);
    if (!pointsToRefresh.length) return;

    markOverlayTracesLoading(pointsToRefresh, 'spectrum');

    const fetchJobs = pointsToRefresh.map(point => ({
      params: buildOverlayFetchParams(point, {
        amp_type: newAmpType,
        ...(featureOverride != null ? { feature: featureOverride } : {})
      })
    }));

    runWithConcurrencyLimit(fetchJobs, 6, ({ params }) =>
      fetchOverlaySpectrumForPoint(params)
    );
  };

  const refreshOverlayPointsWithNewFeature = (newFeature) => {
    const { tag, selectedPoints } = getState();
    if (!tag || !selectedPoints.length) return;

    const pointsToRefresh = selectedPoints.filter(point => point.x);
    if (!pointsToRefresh.length) return;

    // Supersede any in-flight fetches from the previous feature so their
    // (different-keyed) responses cannot merge stale data into the points.
    bumpOverlayEpoch();
    markOverlayTracesLoading(pointsToRefresh, 'spectrum');

    const fetchJobs = pointsToRefresh.map(point => ({
      params: buildOverlayFetchParams(point, { feature: newFeature })
    }));

    runWithConcurrencyLimit(fetchJobs, 6, ({ params }) =>
      fetchOverlaySpectrumForPoint(params)
    );
  };

  const refreshOverlayWaveformsWithNewAmplitudeType = (newAmpType) => {
    const { tag, selectedPoints } = getState();
    if (!tag || !selectedPoints.length) return;

    const pointsToRefresh = selectedPoints.filter(point => point.x);
    if (!pointsToRefresh.length) return;

    markOverlayTracesLoading(pointsToRefresh, 'waveform');

    const fetchJobs = pointsToRefresh.map(point => ({
      params: buildOverlayFetchParams(point, { amp_type: newAmpType })
    }));

    runWithConcurrencyLimit(fetchJobs, 6, ({ params }) =>
      fetchOverlayWaveformForPoint(params)
    );
  };

  const refreshOverlayTraces = (newAmpType, previousAmpType, featureOverride) => {
    // Bump the epoch once for the whole amp-type change so in-flight fetches
    // for the previous amp type are invalidated before the new batch starts.
    // (Both sub-calls capture the new epoch via overlayEpoch at fetch time.)
    bumpOverlayEpoch();
    refreshOverlayPointsWithNewAmplitudeType(newAmpType, previousAmpType, featureOverride);
    refreshOverlayWaveformsWithNewAmplitudeType(newAmpType);
  };

  const retryOverlayTraceForPoint = (point) => {
    if (!point || !point.x) return;
    const traceKeys = getOverlayTraceKeysForChartType(getState().chartType);
    const params = buildOverlayFetchParams(point);
    traceKeys.forEach((traceKey) => {
      if (traceKey === OVERLAY_TRACE_KEYS.SPECTRUM) {
        fetchOverlaySpectrumForPoint(params);
      } else {
        fetchOverlayWaveformForPoint(params);
      }
    });
  };

  const ensureOverlayTraceDataForChartType = (chartType) => {
    const { showOverlay, selectedPoints } = getState();
    if (!showOverlay || !selectedPoints.length) return;

    const traceKeys = getOverlayTraceKeysForChartType(chartType);
    const fetchJobs = [];

    selectedPoints.forEach((point) => {
      if (!point.x) return;
      traceKeys.forEach((traceKey) => {
        if (!pointHasTraceData(point, traceKey)) {
          const traceType = traceKey === OVERLAY_TRACE_KEYS.SPECTRUM ? 'spectrum' : 'waveform';
          const status = point.traceStatus && point.traceStatus[traceType];
          if (status !== 'loading') {
            fetchJobs.push({ point, traceKey });
          }
        }
      });
    });

    if (fetchJobs.length > 0) {
      setState((prevState) => {
        const updatedPoints = prevState.selectedPoints.map((point) => {
          const needsFetch = fetchJobs.some((job) => {
            if (job.point !== point) return false;
            const traceType = job.traceKey === OVERLAY_TRACE_KEYS.SPECTRUM ? 'spectrum' : 'waveform';
            const currentStatus = point.traceStatus && point.traceStatus[traceType];
            return currentStatus !== 'ok';
          });
          if (!needsFetch) return point;

          const nextTraceStatus = { ...(point.traceStatus || {}) };
          fetchJobs.forEach((job) => {
            if (job.point !== point) return;
            const traceType = job.traceKey === OVERLAY_TRACE_KEYS.SPECTRUM ? 'spectrum' : 'waveform';
            if (nextTraceStatus[traceType] !== 'ok') {
              nextTraceStatus[traceType] = 'loading';
            }
          });

          return { ...point, traceStatus: nextTraceStatus };
        });

        return { selectedPoints: updatedPoints };
      });
    }

    runWithConcurrencyLimit(fetchJobs, 6, ({ point, traceKey }) => {
      const params = buildOverlayFetchParams(point);
      if (traceKey === OVERLAY_TRACE_KEYS.SPECTRUM) {
        return fetchOverlaySpectrumForPoint(params);
      }
      return fetchOverlayWaveformForPoint(params);
    });
  };

  return {
    bumpOverlayEpoch,
    isOverlayOperationCurrent,
    logOverlayFetchError,
    buildOverlayFetchParams,
    markOverlayTracesLoading,
    fetchOverlaySpectrumForPoint,
    fetchOverlayWaveformForPoint,
    mergeSpectrumOverlayIntoPoint,
    ensureOverlayTraceDataForChartType,
    refreshOverlayPointsWithNewAmplitudeType,
    refreshOverlayPointsWithNewFeature,
    refreshOverlayWaveformsWithNewAmplitudeType,
    refreshOverlayTraces,
    retryOverlayTraceForPoint,
    findPointIndexByTimestamp,
    getPointTraceStatus,
    updatePointTraceStatus
  };
}
